/*
 * IMPORTS
 */
import React from "react"; // Npm: react.js library.
import PropTypes from "prop-types"; // Npm: Prop types for checking props type.
import Moment from "moment"; // Npm: Moment.js library for date and time formatting.
import _ from "underscore"; // Npm: utility module.
import { toast } from "react-hot-toast"; // Npm: React toastify for notifications.
import { HiArrowPath, HiPlusCircle } from "react-icons/hi2"; // Npm: React icons.
import { connect } from "react-redux"; // Npm: React Redux for state management.
import { useMutation, useQuery } from "@apollo/client"; // Npm: Apollo client for GraphQL queries and mutations.
import { DataGrid } from "@mui/x-data-grid"; // Npm: Material UI DataGrid component.
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  Flex,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from "@chakra-ui/react"; // Npm: Chakra UI components.

/*
 * SIBLINGS
 */
import TableSpinner from "components/TableSpinner";
import DownloadToExcel from "components/DownloadToExcel";
import Modal from "components/Modal";
import RatePlanUpsert from "components/RatePlanUpsert";
import RatePlanDelete from "components/RatePlanDelete";
import FileUpload from "components/FileUpload";
import RatePlanDropDownOptions from "components/RatePlanDropDownOptions";
import PlanFilterOptions from "components/PlanFilterOptions";
import { MemoizedInput } from "components/MemoizedInput";

/*
 * GRAPHS
 */
import RatePlanReadQuery from "./__query__/index.ratePlan.read.query";
import RatePlanUploadMutation from "./__mutation__/index.ratePlan.upload.mutation";

/*
 * STYLES
 */
import { buttonStyle } from "./index.style";

const _PrepareRatePlanUploadFile = (file) => {
  // Const assignment.
  const _extension = file?.name?.split(".")?.pop()?.toLowerCase();

  // Send supported files unchanged; the backend validates and normalizes rows.
  if (["csv", "xlsx"].includes(_extension)) return Promise.resolve(file);

  // Report unsupported formats before sending a GraphQL request.
  return Promise.reject(
    new Error("Only CSV and XLSX rate-plan files are supported."),
  );
};

const Index = ({ passOn }) => {
  // Const assignment.
  const _successFlags = React.useMemo(
    () =>
      Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(
        (i) => i.key,
      ),
    [],
  );

  // Hook assignment.
  const [selectAll, setSelectAll] = React.useState(false);
  const [onlyShowSenderIdBasedRouting, setOnlyShowSenderIdBasedRouting] =
    React.useState(false);
  const [onlyShowPhoneNumberBasedRouting, setOnlyShowPhoneNumberBasedRouting] =
    React.useState(false);
  const [uploadingFile, setUploadingFile] = React.useState(false);
  const [isRefreshing, setIsRefreshing] = React.useState(false);
  const [selectedIds, setSelectedIds] = React.useState([]);
  const [paginationModel, setPaginationModel] = React.useState({
    pageSize: 10,
    page: 0,
  });
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({
    id: false,
  });

  // Debounced search states.
  const [mccSearch, setMccSearchQuery] = React.useState("");
  const [mncSearch, setMncSearchQuery] = React.useState("");
  const [countrySearch, setCountrySearchQuery] = React.useState("");
  const [operatorSearch, setOperatorSearchQuery] = React.useState("");

  const [MutationRatePlanUpload] = useMutation(RatePlanUploadMutation);
  const {
    isOpen: isRatePlanCreateOpen,
    onOpen: onRatePlanCreateOpen,
    onClose: onRatePlanCreateClose,
  } = useDisclosure();
  const {
    isOpen: isRatePlanUpdateOpen,
    onOpen: onRatePlanUpdateOpen,
    onClose: onRatePlanUpdateClose,
  } = useDisclosure();
  const {
    isOpen: isRatePlanDeleteOpen,
    onOpen: onRatePlanDeleteOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();
  const _isPreviousPageRef = React.useRef(false);
  const _paginationModelRef = React.useRef(paginationModel);
  const _rowCountRef = React.useRef(0);

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false;
    setPaginationModel((current) => {
      const nextPaginationModel = {
        ...current,
        page: 0,
      };

      _paginationModelRef.current = nextPaginationModel;

      return nextPaginationModel;
    });
  }, []);

  const _QueryRatePlanReadVariables = React.useMemo(
    () => ({
      rateId: passOn?.rateId,
      mccNameToSearch: mccSearch,
      mncNameToSearch: mncSearch,
      countryNameToSearch: countrySearch,
      operatorNameToSearch: operatorSearch,
      skip: paginationModel.pageSize * paginationModel.page,
      take: paginationModel.pageSize,
    }),
    [
      countrySearch,
      mccSearch,
      mncSearch,
      operatorSearch,
      paginationModel.page,
      paginationModel.pageSize,
      passOn?.rateId,
    ],
  );

  const _QueryRatePlanRead = useQuery(RatePlanReadQuery, {
    variables: _QueryRatePlanReadVariables,
    fetchPolicy: _isPreviousPageRef.current
      ? "cache-first"
      : Object.React.App.fetchPolicy,
  });

  // Refresh from first page so create/upload/delete changes are immediately visible.
  const _RefetchRatePlanReadFromFirstPage = React.useCallback(async () => {
    const nextPaginationModel = {
      ..._paginationModelRef.current,
      page: 0,
    };

    _isPreviousPageRef.current = false;
    _paginationModelRef.current = nextPaginationModel;

    if (0 !== paginationModel.page) {
      setPaginationModel(nextPaginationModel);
    }

    return _QueryRatePlanRead.refetch({
      ..._QueryRatePlanReadVariables,
      skip: 0,
      take: nextPaginationModel.pageSize,
    });
  }, [_QueryRatePlanRead, _QueryRatePlanReadVariables, paginationModel.page]);

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true);

    try {
      await _RefetchRatePlanReadFromFirstPage();
    } finally {
      setIsRefreshing(false);
    }
  }, [_RefetchRatePlanReadFromFirstPage]);

  const _HandlePaginationModelChange = React.useCallback(
    (__paginationModel) => {
      // Only backward pagination with the same page size should prefer cached rows.
      _isPreviousPageRef.current =
        __paginationModel.page < _paginationModelRef.current.page &&
        __paginationModel.pageSize === _paginationModelRef.current.pageSize;

      _paginationModelRef.current = __paginationModel;
      setPaginationModel(__paginationModel);
    },
    [],
  );

  const _HandleRatePlanFileUpload = React.useCallback(
    async (e) => {
      // Const assignment.
      const _file = _.first(e.target.files);

      /*
       * If user has uploaded the rate plan csv then update it.
       * as well.
       */
      if (_file) {
        if (!passOn?.rateId) {
          toast.error("Rate is missing. Please select a rate and try again.");
          e.target.value = "";
          return;
        }

        try {
          // Update loading state.
          setUploadingFile(true);

          // Send the original file; backend handles MNC normalization.
          const _uploadFile = await _PrepareRatePlanUploadFile(_file);

          // Execute mutation.
          const _MutationRatePlanUpload = await MutationRatePlanUpload({
            variables: {
              rateId: passOn.rateId.toString(),
              replaceAll: true,
              thumbnailStoredAt: _uploadFile,
            },
          });

          // Toast message on _MutationRatePlanUpload.
          const _ratePlanUpload = _MutationRatePlanUpload?.data?.RatePlanUpload;
          if (_.isEmpty(_ratePlanUpload)) {
            toast.error("Something is not right. try again after some time.");
            return;
          }

          const _failedRatePlanUpload = _.find(
            _ratePlanUpload,
            (i) => !_successFlags.includes(i?.status),
          );
          const _uploadMessage =
            (_failedRatePlanUpload ?? _.first(_ratePlanUpload))?.message ??
            "Something is not right. try again after some time.";

          if (_failedRatePlanUpload) toast.error(_uploadMessage);
          else toast.success(_uploadMessage);

          await _RefetchRatePlanReadFromFirstPage().catch(() => void 0);
        } catch (uploadError) {
          // Toast error message.
          toast.error(
            uploadError?.message ??
              "The CSV/XLSX file doesn't have the right format. Please check it and try again.",
          );
        } finally {
          // Update loading state.
          setUploadingFile(false);

          e.target.value = "";
        }
      }
    },
    [
      MutationRatePlanUpload,
      _RefetchRatePlanReadFromFirstPage,
      _successFlags,
      passOn?.rateId,
    ],
  );

  const _RatePlanReadData =
    _QueryRatePlanRead?.data ?? _QueryRatePlanRead?.previousData;
  const _QueryRatePlanReadRowCount = _.first(
    _RatePlanReadData?.RatePlanRead,
  )?._totalCount;
  const rowCount = React.useMemo(() => {
    if (
      undefined !== _QueryRatePlanReadRowCount &&
      null !== _QueryRatePlanReadRowCount
    )
      _rowCountRef.current = _QueryRatePlanReadRowCount;

    return _rowCountRef.current;
  }, [_QueryRatePlanReadRowCount]);

  const _tableHeaderHeightRef = React.useRef(0);
  const _isCurrentViewMobile = useBreakpointValue({
    base: false,
    md: false,
    lg: false,
    xl: false,
    sm: true,
    xs: true,
  });

  // Debounce handlers.
  const _onMccInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setMccSearchQuery(e.target.value);
    }, 800),
  ).current;
  const _onMncInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setMncSearchQuery(e.target.value);
    }, 800),
  ).current;
  const _onCountryInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setCountrySearchQuery(e.target.value);
    }, 800),
  ).current;
  const _onOperatorsInputChange = React.useRef(
    _.debounce((e) => {
      _ResetPagination();
      setOperatorSearchQuery(e.target.value);
    }, 800),
  ).current;

  // Cleanup on unmount.
  React.useEffect(() => {
    return () => {
      _onMccInputChange.cancel?.();
      _onMncInputChange.cancel?.();
      _onCountryInputChange.cancel?.();
      _onOperatorsInputChange.cancel?.();
    };
  }, []);

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched.
    if (_QueryRatePlanRead.data) {
      // Reset states.
      setSelectedIds([]);
      setSelectAll(false);
    }
  }, [
    isRatePlanCreateOpen,
    isRatePlanUpdateOpen,
    isRatePlanDeleteOpen,
    _QueryRatePlanRead.data,
  ]);

  // Object assignment.
  const _HandleCheckboxChange = (id) => {
    // Check if id is already selected.
    if (selectedIds.includes(id)) {
      // Remove id from selected ids.
      setSelectedIds(selectedIds.filter((selectedId) => selectedId !== id));
    } else {
      // Add id to selected ids.
      setSelectedIds([...selectedIds, id]);
    }
  };

  const _HandleSelectAll = () => {
    // Set select all to opposite of current value.
    setSelectAll(!selectAll);
    setSelectedIds(
      selectAll
        ? []
        : _RatePlanReadData?.RatePlanRead?.map((item) => item.id) || [],
    );
  };

  // Filter based on senderId or phoneNumber.
  const _rows = React.useMemo(() => {
    if (onlyShowSenderIdBasedRouting) {
      // Filter rows.
      return _RatePlanReadData?.RatePlanRead?.filter(
        (i) => !_.isEmpty(i?.SenderIdDirectory?.displayName),
      );
    }
    if (onlyShowPhoneNumberBasedRouting) {
      // Filter rows.
      return _RatePlanReadData?.RatePlanRead?.filter(
        (i) => !_.isEmpty(i?.PhoneNumberoDirectory?.displayName),
      );
    }

    // Return all rows.
    return _RatePlanReadData?.RatePlanRead;
  }, [
    _RatePlanReadData?.RatePlanRead,
    onlyShowPhoneNumberBasedRouting,
    onlyShowSenderIdBasedRouting,
  ]);

  // Columns definition.
  const columns = React.useMemo(
    () => [
      {
        field: "checkbox",
        headerName: "",
        width: 70,
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        renderHeader: () => (
          <Checkbox
            bg="gray.300"
            onChange={_HandleSelectAll}
            isChecked={selectAll}
          />
        ),
        renderCell: ({ row }) => (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            height="100%"
          >
            <Checkbox
              bg="gray.300"
              isChecked={selectedIds.includes(row.id)}
              onChange={() => _HandleCheckboxChange(row.id)}
            />
          </Box>
        ),
      },
      {
        field: "sno",
        headerName: "S.No.",
        align: "center",
        headerAlign: "center",
        width: 100,
        sortable: false,
        filterable: false,
      },
      {
        field: "id",
        headerName: "ID",
        align: "center",
        headerAlign: "center",
        hide: true,
      },
      {
        field: "rateName",
        headerName: "Rate Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "mcc",
        headerName: "Mcc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 120,
      },
      {
        field: "mnc",
        headerName: "Mnc",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        width: 120,
      },
      {
        field: "countryName",
        headerName: "Country Name",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "operators",
        headerName: "Operators",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 300,
      },
      {
        field: "senderIdDirectory",
        headerName: "SenderId's Directory",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "phoneNumberDirectory",
        headerName: "PhoneNumber's Directory",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 180,
      },
      {
        field: "rate",
        headerName: "Rate",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "createdAt",
        headerName: "CreatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "updatedAt",
        headerName: "UpdatedAt",
        editable: false,
        sortable: true,
        filterable: false,
        align: "center",
        headerAlign: "center",
        width: 150,
      },
      {
        field: "Account.displayName",
        headerName: "Created By",
        editable: false,
        sortable: true,
        filterable: true,
        align: "center",
        headerAlign: "center",
        flex: 1,
        minWidth: 200,
      },
      {
        field: "action",
        headerName: "Actions",
        sortable: false,
        filterable: false,
        align: "center",
        headerAlign: "center",
        disableColumnMenu: true,
        width: 100,
        renderCell: ({ row }) => (
          <RatePlanDropDownOptions
            // ratePlanIds={[...selectedIds, row.id]}
            ratePlanIds={_.union(selectedIds, [row.id])}
            ratePlanId={row.id}
            rateId={row.rateId}
            onRatePlanUpdate={onRatePlanUpdateOpen}
            onRatePlanDelete={onRatePlanDeleteOpen}
            selectedIds={selectedIds}
          />
        ),
      },
    ],
    [selectAll, selectedIds],
  );

  // Rows mapping.
  const rows = React.useMemo(
    () =>
      _.compact(
        _rows?.map((item, index) =>
          _successFlags.includes(item?.status)
            ? {
                id:
                  item?.id ??
                  `rate-plan-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
                rateId: item?.Rate?.id,
                sno: `${index + paginationModel.page * paginationModel.pageSize + 1}.`,
                rateName: item?.Rate?.displayName ?? "-",
                mcc: item?.Mcc?.mcc ?? "-",
                mnc: item?.Mnc?.mnc ?? "-",
                countryName: item?.Mcc?.countryName ?? "-",
                operators: item?.Mnc?.network ?? "-",
                senderIdDirectory: item?.SenderIdDirectory?.displayName ?? "-",
                phoneNumberDirectory:
                  item?.PhoneNumberoDirectory?.displayName ?? "-",
                rate: item?.rate
                  ? `${item?.rate} ${item?.Rate?.currency}`
                  : "-",
                createdAt: Moment(item?.createdAt).format("YYYY-MM-DD"),
                updatedAt: Moment(item?.updatedAt).format("YYYY-MM-DD"),
                "Account.displayName": item?.Account?.displayName ?? "-",
              }
            : null,
        ),
      ) || [],
    [_rows, paginationModel.page, paginationModel.pageSize],
  );

  // Return component.
  return (
    <>
      <Flex className="ratePlan base" h="98vh" overflow="hidden">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          gap={_isCurrentViewMobile ? "12px" : "14px"}
          bg="white"
          h="100%"
          minH={0}
          overflow="hidden"
          p={_isCurrentViewMobile ? "12px" : "22px"}
        >
          {/* Header Section */}
          <Flex
            pb="0px"
            justify="space-between"
            overflow="hidden"
            flexWrap={{ base: "wrap", md: "wrap" }}
            flexShrink={0}
          >
            <Flex gap="12px">
              <Tooltip label="Create new rate plan." fontSize="sm">
                <Button
                  onClick={onRatePlanCreateOpen}
                  leftIcon={<HiPlusCircle />}
                  style={buttonStyle}
                >
                  New Rate Plan
                </Button>
              </Tooltip>
              <Flex zIndex={1000}>
                <PlanFilterOptions
                  onChange={(e) => {
                    // Conditional based toggling.
                    if ("SENDER_ID" === e) {
                      // Only show senderId based routing.
                      setOnlyShowSenderIdBasedRouting(
                        !onlyShowSenderIdBasedRouting,
                      );

                      // Disable other filters.
                      setOnlyShowPhoneNumberBasedRouting(false);
                    } else if ("PHONE_NUMBER" === e) {
                      // Only show phone number based routing.
                      setOnlyShowPhoneNumberBasedRouting(
                        !onlyShowPhoneNumberBasedRouting,
                      );

                      // Disable other filters.
                      setOnlyShowSenderIdBasedRouting(false);
                    } else {
                      // Show all.
                      setOnlyShowSenderIdBasedRouting(false);
                      setOnlyShowPhoneNumberBasedRouting(false);
                    }
                  }}
                />
              </Flex>
            </Flex>
            <Flex gap="12px">
              <Tooltip label="Refresh rate plans." fontSize="sm">
                <IconButton
                  aria-label="Refresh rate plans"
                  icon={<HiArrowPath />}
                  isDisabled={isRefreshing}
                  onClick={_HandleRefresh}
                  style={buttonStyle}
                />
              </Tooltip>
              <FileUpload
                uploadingFile={uploadingFile}
                label="Upload RatePlan"
                onChange={_HandleRatePlanFileUpload}
                accept=".csv,.xlsx"
                disabled={uploadingFile}
              />
              <DownloadToExcel
                cellsData={
                  0 === _rows?.length ||
                  !_.every(
                    _.pluck(_rows, "status"),
                    (i) => "READ_SUCCESSFUL" === i,
                  )
                    ? []
                    : _rows?.map((item, __index) => ({
                        "S.No.": `${__index + paginationModel.pageSize * paginationModel.page + 1}.`,
                        "Rate Name": item?.Rate?.displayName ?? "-",
                        Mcc: item?.Mcc?.mcc ?? "-",
                        Mnc: item?.Mnc?.mnc ?? "-",
                        "Country Name": item?.Mcc?.countryName ?? "-",
                        Operators: item?.Mnc?.network ?? "-",
                        Rate: item?.rate ? item?.rate : "-",
                        CreatedAt: Moment(item.createdAt).format("YYYY-MM-DD"),
                        UpdatedAt: Moment(item.updatedAt).format("YYYY-MM-DD"),
                      }))
                }
                headersData={[
                  "S.No.",
                  "Rate Name",
                  "Mcc",
                  "Mnc",
                  "Country Name",
                  "Operators",
                  "Rate",
                  "CreatedAt",
                  "UpdatedAt",
                ].map((i) => ({ key: i, label: i }))}
              />
            </Flex>
          </Flex>

          {/* Filter Section */}
          <Flex
            gap="12px"
            flexWrap="wrap"
            bg="white"
            p="10px"
            borderRadius="12px"
            border="1px solid #C5CFE8"
            alignItems="flex-start"
            flexShrink={0}
          >
            <Box
              minW={_isCurrentViewMobile ? "100%" : "150px"}
              w={_isCurrentViewMobile ? "100%" : "200px"}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mcc
              </Text>
              <MemoizedInput
                placeholder="Search by Mcc"
                className="filter searchInput"
                defaultValue={mccSearch}
                onChange={_onMccInputChange}
              />
            </Box>

            <Box
              minW={_isCurrentViewMobile ? "100%" : "150px"}
              w={_isCurrentViewMobile ? "100%" : "200px"}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Mnc
              </Text>
              <MemoizedInput
                placeholder="Search by Mnc"
                className="filter searchInput"
                defaultValue={mncSearch}
                onChange={_onMncInputChange}
              />
            </Box>

            <Box
              minW={_isCurrentViewMobile ? "100%" : "150px"}
              w={_isCurrentViewMobile ? "100%" : "200px"}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Country Name
              </Text>
              <MemoizedInput
                placeholder="Search by Country"
                className="filter searchInput"
                defaultValue={countrySearch}
                onChange={_onCountryInputChange}
              />
            </Box>

            <Box
              minW={_isCurrentViewMobile ? "100%" : "150px"}
              w={_isCurrentViewMobile ? "100%" : "200px"}
            >
              <Text fontSize="14px" fontWeight={600} color="rgb(141, 151, 181)">
                Operators
              </Text>
              <MemoizedInput
                placeholder="Search by Operator"
                className="filter searchInput"
                defaultValue={operatorSearch}
                onChange={_onOperatorsInputChange}
              />
            </Box>
          </Flex>

          <Box
            h={_isCurrentViewMobile ? "360px" : "calc(98vh - 220px)"}
            minH={_isCurrentViewMobile ? "360px" : "420px"}
            maxH={_isCurrentViewMobile ? "360px" : "calc(98vh - 220px)"}
            w="100%"
            overflow="hidden"
            flexShrink={0}
          >
            {/* MUI DataGrid */}
            <DataGrid
              rows={rows}
              columns={columns}
              rowHeight={48}
              rowCount={rowCount}
              paginationMode="server"
              paginationModel={paginationModel}
              onPaginationModelChange={_HandlePaginationModelChange}
              pageSizeOptions={[10, 30, 50, 100]}
              slots={{
                loadingOverlay: () => (
                  <TableSpinner
                    isLoading={true}
                    isEmpty={false}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noResultsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
                noRowsOverlay: () => (
                  <TableSpinner
                    isLoading={false}
                    isEmpty={true}
                    chopHeightFromHundredPercentage={
                      _tableHeaderHeightRef?.current?.clientHeight
                    }
                  />
                ),
              }}
              disableRowSelectionOnClick
              columnVisibilityModel={columnVisibilityModel}
              onColumnVisibilityModelChange={(newModel) =>
                setColumnVisibilityModel(newModel)
              }
              loading={_QueryRatePlanRead?.loading}
              sx={{ height: "100%" }}
            />
          </Box>
        </Flex>

        {/* Modals */}
        <Modal
          size="2xl"
          title="Rate Plan Create"
          isOpen={isRatePlanCreateOpen}
          onClose={onRatePlanCreateClose}
          isCentered={false}
        >
          <RatePlanUpsert
            isCreateOnly={true}
            isOpen={isRatePlanCreateOpen}
            onClose={onRatePlanCreateClose}
            refetch={_RefetchRatePlanReadFromFirstPage}
          />
        </Modal>
        <Modal
          size="2xl"
          title="Rate Plan Update"
          isOpen={isRatePlanUpdateOpen}
          onClose={onRatePlanUpdateClose}
          isCentered={true}
        >
          <RatePlanUpsert
            isOpen={isRatePlanUpdateOpen}
            onClose={onRatePlanUpdateClose}
          />
        </Modal>
        <Modal
          size="md"
          title="Rate Plan Delete"
          isOpen={isRatePlanDeleteOpen}
          onClose={onDeleteModalClose}
        >
          <RatePlanDelete
            onClose={onDeleteModalClose}
            refetch={_RefetchRatePlanReadFromFirstPage}
          />
        </Modal>
      </Flex>
    </>
  );
}


/*
 * PROPTYPES
 */
Index.propTypes = {
  passOn: PropTypes.object,
};

/*
 * REDUX
 */
const _MapStateToProps = (__state) => ({ passOn: __state.PassOn });

/*
 * EXPORT
 */
export default connect(_MapStateToProps)(Index);
